import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  Feature,
  FeatureActionFields,
  FeatureCommands,
  FeatureNames,
  FeaturePropertyKey,
  VentilationQuickSelectionsCommands,
  VentilationQuickSelectionsProperties,
} from '@vi-iot/client';

import { NO_VALUE } from '@pipes';
import { GTMService } from '@services';
import { InstallationFeaturesStore, VentilationQuickSelectionsDetails } from '@store';
import { CustomTranslationsService } from '../../../../operational-data-common/translations';
import { QuickSelectionsModes } from 'e2e/views/installation-details-ventilation/ventilation';
import { Value2Pipe } from 'src/app/operational-data-common/feature-pipes/value-strategy-based/value2.pipe';

@Component({
  selector: 'app-ventilation-quick-selections',
  templateUrl: './ventilation-quick-selections.component.html',
  styleUrls: ['./ventilation-quick-selections.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VentilationQuickSelectionsComponent implements OnInit, OnDestroy {
  FeatureNames = FeatureNames;
  FeatureCommands = FeatureCommands;

  ventilationQuickSelectionsView$: BehaviorSubject<{
    isActive: boolean;
    remainingTime?: number;
  }> = new BehaviorSubject({ isActive: false });
  ventilationQuickSelectionsDetails$: Observable<VentilationQuickSelectionsDetails>;

  ventilationTimeoutHours: number;
  ventilationTimeoutMinutes: number;
  readonly VENTILATION_MAX_HOURS = 24;
  readonly VENTILATION_ZERO_VALUE = 0;

  quickSelectionCheckedProgram: string;
  quickSelectionActiveProgram: string;

  hours: number[] = Array(24)
    .fill(0)
    .map((_v, i) => i + 1);
  minutes: number[] = Array(59)
    .fill(0)
    .map((_v, i) => i + 1);

  NO_VALUE = NO_VALUE;

  private readonly MINUTES_IN_HOUR: number = 60;
  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private gtmService: GTMService,
    protected customTranslationsService: CustomTranslationsService,
    private installationFeaturesStore: InstallationFeaturesStore,
    private value2Pipe: Value2Pipe
  ) {}

  ngOnInit(): void {
    this.ventilationQuickSelectionsDetails$ = this.installationFeaturesStore.getVentilationQuickSelectionsDetails();

    this.ventilationQuickSelectionsDetails$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((details) => {
        this.setQuickSelectionsViewState(details.active);
        this.setDefaultRuntime(details.defaultRuntime);

        this.ventilationQuickSelectionsView$.next({
          isActive: !!details.active,
          remainingTime: details.active?.properties?.remainingRuntime?.value,
        });
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  quickSelectionActivate(featureName: string): void {
    this.installationFeaturesStore.updateFeature(
      featureName,
      this.ventilationTimeoutMinutes + this.ventilationTimeoutHours * this.MINUTES_IN_HOUR,
      null,
      FeatureActionFields.timeout,
      FeatureCommands.activate
    );
  }

  quickSelectionDeactivate(featureName: string): void {
    this.installationFeaturesStore.updateFeature(
      featureName,
      null,
      null,
      null,
      FeatureCommands.deactivate
    );
  }

  doQuickSelectionAction(): void {
    if (this.quickSelectionActiveProgram) {
      this.quickSelectionDeactivate(this.quickSelectionActiveProgram);
      this.trackVentilationQuickSelectionProgram('onDeactivate', this.quickSelectionActiveProgram);
    } else {
      this.quickSelectionActivate(this.quickSelectionCheckedProgram);
      this.trackVentilationQuickSelectionProgram('onActivate', this.quickSelectionCheckedProgram);
    }
  }

  onVentilationQuickSelectionHoursChange(event: MatSelectChange): void {
    if (event.value === this.VENTILATION_MAX_HOURS) {
      this.ventilationTimeoutMinutes = 0;
    }
  }

  onVentilationQuickSelectionProgramChange(event: string): void {
    const value2Pipe = this.value2Pipe;

    this.quickSelectionCheckedProgram = event;

    this.installationFeaturesStore
      .selectInstallationDeviceFeature(event)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((feature) => {
        if (feature) {
          const defaultRuntime = value2Pipe.transform(feature, FeaturePropertyKey.defaultRuntime);
          this.setDefaultRuntime(parseInt(defaultRuntime));
        }
      });

    this.trackVentilationQuickSelectionProgram(
      'onSelectionChange',
      this.quickSelectionCheckedProgram
    );
  }

  trackVentilationQuickSelectionProgram(action: string, featureName: string): void {
    const programName =
      (featureName === FeatureNames.ventilationQuickmodesSilent &&
        QuickSelectionsModes.silentVentilation) ||
      (featureName === FeatureNames.ventilationQuickmodesForcedLevelFour &&
        QuickSelectionsModes.intensive) ||
      (featureName === FeatureNames.ventilationQuickmodesTemporaryShutdown &&
        QuickSelectionsModes.temporaryShutdown);

    this.gtmService.track(
      action,
      `Ventilation's quick selection - ${programName}`,
      `Ventilation's quick selection`
    );
  }

  private setQuickSelectionsViewState(
    activeFeature: Feature<VentilationQuickSelectionsProperties, VentilationQuickSelectionsCommands>
  ): void {
    if (activeFeature) {
      this.quickSelectionCheckedProgram = activeFeature?.feature;
      this.quickSelectionActiveProgram = activeFeature?.feature;
    } else {
      this.quickSelectionActiveProgram = null;
      if (this.quickSelectionCheckedProgram === undefined) {
        this.quickSelectionCheckedProgram = FeatureNames.ventilationQuickmodesSilent;
      }
    }
  }

  private setDefaultRuntime(defaultRuntime: number): void {
    this.ventilationTimeoutHours = Math.floor(defaultRuntime / this.MINUTES_IN_HOUR);
    this.ventilationTimeoutMinutes = defaultRuntime % this.MINUTES_IN_HOUR;
  }
}
